import { CloudProvider, InstanceStatus, TaniumInstance } from '../types';
import { DomainSuffix } from './constants';

// An instance whose on-prem license the tenant can fetch: either a pending offer that has not been
// claimed yet, or a linked on-prem instance with a recorded downloadable license.
export const isOnPremLicenseDownloadable = (instance: TaniumInstance): boolean => {
  return Boolean(instance.isAvailable || (!instance.isCloudInstance && instance.downloadable));
};

// A linked Tanium Cloud instance, as opposed to a pending offer that has not been claimed or an on-prem deployment.
export const isLinkedCloudInstance = (instance: TaniumInstance): boolean =>
  !instance.isAvailable && instance.isCloudInstance && !!instance.cloudProvider;

// Links into an instance's cloud portal (CMP) only work while the instance is Active.
export const canNavigateToCmp = (instance: TaniumInstance): boolean => instance.status === InstanceStatus.Active;

// A deployment's hostnames arrive as one comma-separated string (the license file's `servers` list),
// so callers that render or count them split it here. Blank entries are dropped: the service trims
// the whole value but not the gaps around the separators.
export const parseHostnames = (hostnames?: string): string[] => {
  if (!hostnames) return [];
  return hostnames
    .split(',')
    .map((hostname) => hostname.trim())
    .filter(Boolean);
};

// Returns the current TIER's domain suffix
export const getDomainSuffix = (): DomainSuffix => {
  let domainSuffix = DomainSuffix.PRD;
  const browserURL = window.location.host.toLowerCase();
  // order is important since the dev host contains the stg host
  if (browserURL.includes(DomainSuffix.DEV) || browserURL.includes('localhost')) {
    domainSuffix = DomainSuffix.DEV;
  } else if (browserURL.includes(DomainSuffix.STG)) {
    domainSuffix = DomainSuffix.STG;
  } else if (browserURL.includes(DomainSuffix.DEV_GOV)) {
    domainSuffix = DomainSuffix.DEV_GOV;
  } else if (browserURL.includes(DomainSuffix.STG_GOV)) {
    domainSuffix = DomainSuffix.STG_GOV;
  } else if (browserURL.includes(DomainSuffix.PRD_GOV)) {
    domainSuffix = DomainSuffix.PRD_GOV;
  }
  return domainSuffix;
};

export const getCMPLink = (ceDomain: string, cloudProvider: CloudProvider, route = ''): string => {
  const browserURL = window.location.host.toLowerCase();
  if (browserURL.indexOf('localhost') > -1) return 'http://localhost:3000';
  return cloudProvider === CloudProvider.AZURE || cloudProvider === CloudProvider.Gov_Azure
    ? `https://${ceDomain}/portal/#/${route}`
    : `https://portal.${ceDomain}/#/${route}`;
};

export const getTaniumConsoleLink = (ceDomain: string): string => {
  return `https://${ceDomain}`;
};

export const getArtifactsLink = (): string => {
  const browserURL = window.location.host.toLowerCase();
  if (browserURL.indexOf('localhost') > -1) return 'http://localhost:3000';
  const domainSuffix = getDomainSuffix();
  return `https://artifacts-v2${domainSuffix}`;
};

const CROSS_CLOUD_SUFFIX: Record<DomainSuffix, DomainSuffix> = {
  [DomainSuffix.PRD]: DomainSuffix.PRD_GOV,
  [DomainSuffix.STG]: DomainSuffix.STG_GOV,
  [DomainSuffix.DEV]: DomainSuffix.DEV_GOV,
  [DomainSuffix.PRD_GOV]: DomainSuffix.PRD,
  [DomainSuffix.STG_GOV]: DomainSuffix.STG,
  [DomainSuffix.DEV_GOV]: DomainSuffix.DEV,
};

// gets the Commercial URL for USG AMP instances and gets the USG URL for Commercial instances
export const getCrossCloudAmpUrl = (): string => {
  return `https://account${CROSS_CLOUD_SUFFIX[getDomainSuffix()]}`;
};
